import { useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, IconButton, Button, Chip, Tooltip } from '@mui/material';
import { CircularProgress, Box } from '@mui/material';
import { getIcon } from '../../../utils/IconHandler';
import { useTranslation } from 'react-i18next';
import './Locations.scss';
import ManageTerminalsModal from './ManageTerminalsModal';
import { permissionOptions } from '../../../utils/Enums';

const LocationTable = ({ 
    locations, 
    businessUnits = [],
    onAdd, 
    onEdit, 
    onEditSchedule, 
    onDelete, 
    onAssignUsers, 
    onPrinterSettings,
    loading,
    isSuperAdmin = false
}) => {

    const { t } = useTranslation();

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const userPermissions = JSON.parse(localStorage.getItem('permissions')) || [];
    const hasPermission = (permissionLabel) => {
        const permission = permissionOptions.find((option) => option.label === permissionLabel);
        return permission ? userPermissions.includes(permission.id) : false;
    };
    const canCreateLocation = isSuperAdmin;
    const canUpdateLocation = hasPermission('UPDATE_LOCATION');
    const canDeleteLocation = hasPermission('DELETE_LOCATION');
    const canUpdateStaff = hasPermission('UPDATE_STAFF');
    const canManageTerminals = hasPermission('MANAGE_TERMINALS');
    const canUpdateSchedule = hasPermission('UPDATE_SCHEDULE');
    const canUpdatePrinterSettings = hasPermission('UPDATE_SETTINGS') || canManageTerminals || canUpdateLocation;
    const showActions = canUpdateStaff || canManageTerminals || canUpdateLocation || canUpdatePrinterSettings || canUpdateSchedule || canDeleteLocation;

    const [isManageTerminalsOpen, setIsManageTerminalsOpen] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [selectedLocationTerminals, setSelectedLocationTerminals] = useState([]);
    const getBusinessUnitName = (businessUnitId) => {
        if (!businessUnitId) return t('location_without_franchise');
        return businessUnits.find((unit) => unit.id === businessUnitId)?.name || t('location_franchise');
    };


    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleManageTerminals = (location) => {
        setSelectedLocation(location);
        setSelectedLocationTerminals(location.terminals || []);
        setIsManageTerminalsOpen(true);
    };

    return (
        <>
        <div className="header-container">
            {canCreateLocation && (
                <Button className="right_button_table" variant="outlined" startIcon={getIcon('add')} onClick={onAdd}>
                    {t('add_location')}
                </Button>
            )}
        </div>
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer component={Paper}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ minWidth: 980 }}>
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                        <TableRow>
                            <TableCell width="19%"><span className="roboto-regular">{t('name')}</span></TableCell>
                            <TableCell width="23%" align="center"><span className="roboto-regular">{t('address')}</span></TableCell>
                            <TableCell width="14%" align="center"><span className="roboto-regular">{t('manager')}</span></TableCell>
                            <TableCell width="10%" align="center"><span className="roboto-regular">{t('location_franchise')}</span></TableCell>
                            <TableCell width="12%" align="center"><span className="roboto-regular">{t('type_location')}</span></TableCell>
                            {showActions && (
                                <TableCell width="22%" className="location-actions-header" align="right"><span className="roboto-regular">{t('actions')}</span></TableCell>
                            )}
                        </TableRow>
                        </TableHead>
                        <TableBody>
                        {loading ? (
                            <TableRow>
                            <TableCell colSpan={showActions ? 6 : 5} align="center">
                                <Box py={3}>
                                <CircularProgress />
                                </Box>
                            </TableCell>
                            </TableRow>
                        ) : (
                            locations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((location) => (
                                <TableRow key={location.id}>
                                    <TableCell>
                                        <Tooltip title={location.name} placement="top">
                                            <span className="roboto-light location-cell-text location-name-cell">{location.name}</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title={location.address} placement="top">
                                            <span className="roboto-light location-cell-text location-address-cell">{location.address}</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="center">
                                        {location.manager ? (
                                            <Tooltip title={`${location.manager.firstName} ${location.manager.lastName}`} placement="top">
                                                <span className="roboto-light location-cell-text location-manager-cell">{location.manager.firstName} {location.manager.lastName}</span>
                                            </Tooltip>
                                        ) : (
                                            <span className="roboto-light location-cell-text location-manager-cell">-</span>
                                        )}
                                    </TableCell>
                                    <TableCell align="center"><Chip size="small" label={getBusinessUnitName(location.businessUnitId)} /></TableCell>
                                    <TableCell align="center"><span className="roboto-light">{location.isPlant ? <Chip label={t('plant')} color="success" /> : <Chip label={t('location')} color="primary" />}</span></TableCell>
                                    {showActions && (
                                        <TableCell align="right" className="location-actions-cell">
                                            {canUpdateStaff && <IconButton size="small" onClick={() => onAssignUsers(location)}>{getIcon('people')}</IconButton>}
                                            {canManageTerminals && <IconButton size="small" onClick={() => handleManageTerminals(location)}>{getIcon('terminal')}</IconButton>}
                                            {canUpdateLocation && <IconButton size="small" onClick={() => onEdit(location)}>{getIcon('settings')}</IconButton>}
                                            {canUpdatePrinterSettings && <IconButton size="small" onClick={() => onPrinterSettings(location)}>{getIcon('printer')}</IconButton>}
                                            {canUpdateSchedule && <IconButton size="small" onClick={() => onEditSchedule(location)}>{getIcon('time')}</IconButton>}
                                            {canDeleteLocation && <IconButton size="small" onClick={() => onDelete(location)}>{getIcon('trash')}</IconButton>}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))
                        )}
                        </TableBody>
                    </Table>
                </Box>
            </Box>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={locations.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Paper>
        <ManageTerminalsModal
            open={isManageTerminalsOpen}
            onClose={() => setIsManageTerminalsOpen(false)}
            locationId={selectedLocation?.id}
            terminals={selectedLocationTerminals}
        />
        </>
    );
};

export default LocationTable;
